import { type FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useRevenueShareReportsQuery } from "@/partner/entities/billing";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { interpolationFormat } from "@/partner/shared/lib";
import { ErrorMessage } from "@/shared/components";
import {
  formatAmountObject,
  formatCurrency,
} from "@/partner/features/products";
import {
  revenueShareReportStatusColor,
  revenueShareReportStatusKey,
} from "../model/paymentStatus";
import { DownloadReportButton } from "@/partner/features/reports";
import { ReportTypeType } from "@/types/graphql/partner.generated";

// Черновики (status draft) в списке не появляются: их отсекает резолвер
// запроса на бэкенде, партнёру они не отдаются ни при каком фильтре. У
// раздела отчётов нет своего фильтра (в отличие от счетов): сид не заводит
// под него отдельных ключей `billing.reports.filter.*`, страница отчётов
// показывает всю историю с пагинацией.
export const BillingReports: FC = () => {
  const { t, i18n } = useTranslation();
  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
  } = useFilter();

  const { data, loading, error } = useRevenueShareReportsQuery({
    variables: { page, rowsPerPage },
    fetchPolicy: "cache-and-network",
  });

  const rows = data?.revenueShareReports?.nodes ?? [];
  const count = data?.revenueShareReports?.count ?? 0;

  const emptyValue = t("common.emptyDash");

  const formatStatus = (status: string): string =>
    t(`billing.reports.status.${revenueShareReportStatusKey(status)}`);

  if (error) return <ErrorMessage error={error} />;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (count === 0) {
    return <EmptyListCard title={t("billing.reports.empty")} />;
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="flex-end">
        <DownloadReportButton
          variant="card"
          type={ReportTypeType.RevenueShareReportsType}
          filter={{}}
        />
      </Stack>
      <Card elevation={0} sx={{ overflowX: "auto" }}>
        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell>{t("billing.reports.columns.period")}</TableCell>
              <TableCell align="right">
                {t("billing.reports.columns.sales")}
              </TableCell>
              <TableCell align="right">
                {t("billing.reports.columns.fee")}
              </TableCell>
              <TableCell align="right">
                {t("billing.reports.columns.refunds")}
              </TableCell>
              <TableCell align="right">
                {t("billing.reports.columns.disputes")}
              </TableCell>
              <TableCell align="right">
                {t("billing.reports.columns.corrections")}
              </TableCell>
              <TableCell align="right">
                {t("billing.reports.columns.total")}
              </TableCell>
              <TableCell>{t("billing.reports.columns.status")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              // Корректировки могут быть и в плюс, и в минус (см. схему): без
              // описаний сумма выглядела бы как ошибка выставления, поэтому
              // список каждой построчно уходит в подсказку.
              const correctionsTotal = row.corrections.reduce(
                (sum, correction) => sum + correction.amount.value,
                0
              );

              return (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="body2" noWrap>
                      {interpolationFormat(
                        `${row.period}-01`,
                        "month",
                        i18n.language
                      )}
                    </Typography>
                  </TableCell>

                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="body2" noWrap>
                      {formatAmountObject(row.salesAmount)}
                    </Typography>
                  </TableCell>

                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="body2" noWrap>
                      {formatAmountObject(row.feeAmount)}
                    </Typography>
                  </TableCell>

                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="body2" noWrap>
                      {formatAmountObject(row.refundsAmount)}
                    </Typography>
                  </TableCell>

                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="body2" noWrap>
                      {formatAmountObject(row.disputesAmount)}
                    </Typography>
                  </TableCell>

                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {row.corrections.length === 0 ? (
                      <Typography variant="body2">{emptyValue}</Typography>
                    ) : (
                      // title строкой, а не JSX: MUI кладёт строковый title в
                      // aria-label клонированного ребёнка (доступно и в
                      // статической разметке), а произвольный ReactNode
                      // рисуется только всплывающим Popper'ом по наведению,
                      // которого нет ни в SSR, ни до открытия по hover.
                      <Tooltip
                        title={row.corrections
                          .map(
                            (correction) =>
                              `${correction.description}: ${formatAmountObject(correction.amount)}`
                          )
                          .join("; ")}
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            textDecoration: "underline dotted",
                            cursor: "help",
                          }}
                        >
                          {formatCurrency(
                            correctionsTotal,
                            row.reportAmount.currency
                          )}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>

                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {formatAmountObject(row.reportAmount)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      color={revenueShareReportStatusColor(row.status)}
                      label={formatStatus(row.status)}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={count}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 100]}
          labelRowsPerPage={t("common.pagination.rowsPerPage")}
          labelDisplayedRows={({ from, to, count }) =>
            t("common.pagination.displayedRows", {
              from,
              to,
              count:
                count !== -1 ? count : t("common.pagination.moreThan", { to }),
            })
          }
        />
      </Card>
    </Stack>
  );
};
